import { z } from "zod/v4";
import { BooleanSchema, FunctionSchema } from "./ea.schema.helpers";

export const SortOrderSchema = z.enum(["asc", "desc"]);
export const QueryModeSchema = z.enum(["default", "insensitive"]);
export const NullsOrderSchema = z.enum(["first", "last"]);

const SortOrderInputSchema = z
  .object({
    sort: z.lazy(() => SortOrderSchema),
    nulls: z.lazy(() => NullsOrderSchema).optional(),
  })
  .strict();

export function generateFilterSelectSchema<Schema extends z.ZodObject>(schema: Schema) {
  const entries = Object.entries(schema.shape) as [keyof Schema["shape"], z.ZodTypeAny][];
  const newProps = entries.reduce(
    (acc, [key, value]) => {
      acc[key] = z
        .preprocess((value) => (value === "true" ? true : value ? true : false), z.boolean())
        .optional();
      return acc;
    },
    {} as {
      [key in keyof Schema["shape"]]: z.ZodOptional<
        z.ZodPipe<z.ZodTransform<boolean, unknown>, z.ZodBoolean>
      >;
    },
  );
  return z.object(newProps);
}

export function generateFilterSortSchema<Schema extends z.ZodObject>(schema: Schema) {
  const entries = Object.entries(schema.shape) as [keyof Schema["shape"], z.ZodTypeAny][];
  const tsHack = z
    .union([z.lazy(() => SortOrderSchema), z.lazy(() => SortOrderInputSchema)])
    .optional();
  const newProps = entries.reduce(
    (acc, [key, value]) => {
      acc[key] = z
        .union([z.lazy(() => SortOrderSchema), z.lazy(() => SortOrderInputSchema)])
        .optional();
      return acc;
    },
    {} as {
      [key in keyof Schema["shape"]]: typeof tsHack;
    },
  );
  const newSchema = z.object(newProps).strict();

  return z.union([newSchema.array(), newSchema]).optional();
}

export const IntFilterSchema = z
  .object({
    equals: z.coerce.number().optional(),
    in: z.coerce.number().array().optional(),
    notIn: z.coerce.number().array().optional(),
    lt: z.coerce.number().optional(),
    lte: z.coerce.number().optional(),
    gt: z.coerce.number().optional(),
    gte: z.coerce.number().optional(),
    not: z.union([z.coerce.number(), z.lazy(() => NestedIntFilterSchema)]).optional(),
  })
  .strict();

export const IntNullableFilterSchema = z
  .object({
    equals: z.coerce.number().optional().nullable(),
    in: z.coerce.number().array().optional().nullable(),
    notIn: z.coerce.number().array().optional().nullable(),
    lt: z.coerce.number().optional(),
    lte: z.coerce.number().optional(),
    gt: z.coerce.number().optional(),
    gte: z.coerce.number().optional(),
    not: z
      .union([z.coerce.number(), z.lazy(() => NestedIntNullableFilterSchema)])
      .optional()
      .nullable(),
  })
  .strict();

export const NestedIntFilterSchema = z
  .object({
    equals: z.coerce.number().optional(),
    in: z.coerce.number().array().optional(),
    notIn: z.coerce.number().array().optional(),
    lt: z.coerce.number().optional(),
    lte: z.coerce.number().optional(),
    gt: z.coerce.number().optional(),
    gte: z.coerce.number().optional(),
    not: z.union([z.coerce.number(), z.lazy(() => NestedIntFilterSchema)]).optional(),
  })
  .strict();

export const NestedIntNullableFilterSchema = z
  .object({
    equals: z.coerce.number().optional().nullable(),
    in: z.coerce.number().array().optional().nullable(),
    notIn: z.coerce.number().array().optional().nullable(),
    lt: z.coerce.number().optional(),
    lte: z.coerce.number().optional(),
    gt: z.coerce.number().optional(),
    gte: z.coerce.number().optional(),
    not: z
      .union([z.coerce.number(), z.lazy(() => NestedIntNullableFilterSchema)])
      .optional()
      .nullable(),
  })
  .strict();

export const BigIntFilterSchema = z
  .object({
    equals: z.bigint().optional(),
    in: z.bigint().array().optional(),
    notIn: z.bigint().array().optional(),
    lt: z.bigint().optional(),
    lte: z.bigint().optional(),
    gt: z.bigint().optional(),
    gte: z.bigint().optional(),
    not: z.union([z.bigint(), z.lazy(() => NestedBigIntFilterSchema)]).optional(),
  })
  .strict();

export const NestedBigIntFilterSchema = z
  .object({
    equals: z.bigint().optional(),
    in: z.bigint().array().optional(),
    notIn: z.bigint().array().optional(),
    lt: z.bigint().optional(),
    lte: z.bigint().optional(),
    gt: z.bigint().optional(),
    gte: z.bigint().optional(),
    not: z.union([z.bigint(), z.lazy(() => NestedBigIntFilterSchema)]).optional(),
  })
  .strict();

export const NestedBigIntNullableFilterSchema = z
  .object({
    equals: z.bigint().optional().nullable(),
    in: z.bigint().array().optional().nullable(),
    notIn: z.bigint().array().optional().nullable(),
    lt: z.bigint().optional(),
    lte: z.bigint().optional(),
    gt: z.bigint().optional(),
    gte: z.bigint().optional(),
    not: z
      .union([z.bigint(), z.lazy(() => NestedBigIntNullableFilterSchema)])
      .optional()
      .nullable(),
  })
  .strict();

export const StringFilterSchema = z
  .object({
    equals: z.string().optional(),
    in: z.string().array().optional(),
    notIn: z.string().array().optional(),
    lt: z.string().optional(),
    lte: z.string().optional(),
    gt: z.string().optional(),
    gte: z.string().optional(),
    contains: z.string().optional(),
    startsWith: z.string().optional(),
    endsWith: z.string().optional(),
    mode: z.lazy(() => QueryModeSchema).optional(),
    not: z.union([z.string(), z.lazy(() => NestedStringFilterSchema)]).optional(),
  })
  .strict();

export const NestedStringFilterSchema = z
  .object({
    equals: z.string().optional(),
    in: z.string().array().optional(),
    notIn: z.string().array().optional(),
    lt: z.string().optional(),
    lte: z.string().optional(),
    gt: z.string().optional(),
    gte: z.string().optional(),
    contains: z.string().optional(),
    startsWith: z.string().optional(),
    endsWith: z.string().optional(),
    not: z.union([z.string(), z.lazy(() => NestedStringFilterSchema)]).optional(),
  })
  .strict();

export const StringNullableFilterSchema = z
  .object({
    equals: z.string().optional().nullable(),
    in: z.string().array().optional().nullable(),
    notIn: z.string().array().optional().nullable(),
    lt: z.string().optional(),
    lte: z.string().optional(),
    gt: z.string().optional(),
    gte: z.string().optional(),
    contains: z.string().optional(),
    startsWith: z.string().optional(),
    endsWith: z.string().optional(),
    mode: z.lazy(() => QueryModeSchema).optional(),
    not: z
      .union([z.string(), z.lazy(() => NestedStringNullableFilterSchema)])
      .optional()
      .nullable(),
  })
  .strict();

export const NestedStringNullableFilterSchema = z
  .object({
    equals: z.string().optional().nullable(),
    in: z.string().array().optional().nullable(),
    notIn: z.string().array().optional().nullable(),
    lt: z.string().optional(),
    lte: z.string().optional(),
    gt: z.string().optional(),
    gte: z.string().optional(),
    contains: z.string().optional(),
    startsWith: z.string().optional(),
    endsWith: z.string().optional(),
    not: z
      .union([z.string(), z.lazy(() => NestedStringNullableFilterSchema)])
      .optional()
      .nullable(),
  })
  .strict();

export const BoolFilterSchema = z
  .object({
    equals: BooleanSchema.optional(),
    not: z.union([BooleanSchema, z.lazy(() => NestedBoolFilterSchema)]).optional(),
  })
  .strict();

export const NestedBoolFilterSchema = z
  .object({
    equals: BooleanSchema.optional(),
    not: z.union([BooleanSchema, z.lazy(() => NestedBoolFilterSchema)]).optional(),
  })
  .strict();

export const DateTimeFilterSchema = z
  .object({
    equals: z.coerce.date().optional(),
    in: z.coerce.date().array().optional(),
    notIn: z.coerce.date().array().optional(),
    lt: z.coerce.date().optional(),
    lte: z.coerce.date().optional(),
    gt: z.coerce.date().optional(),
    gte: z.coerce.date().optional(),
    not: z.union([z.coerce.date(), z.lazy(() => NestedDateTimeFilterSchema)]).optional(),
  })
  .strict();

export const NestedDateTimeFilterSchema = z
  .object({
    equals: z.coerce.date().optional(),
    in: z.coerce.date().array().optional(),
    notIn: z.coerce.date().array().optional(),
    lt: z.coerce.date().optional(),
    lte: z.coerce.date().optional(),
    gt: z.coerce.date().optional(),
    gte: z.coerce.date().optional(),
    not: z.union([z.coerce.date(), z.lazy(() => NestedDateTimeFilterSchema)]).optional(),
  })
  .strict();

export const InputJsonValueSchema = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.object({ toJSON: FunctionSchema(z.function({ input: z.tuple([]), output: z.any() })) }),
    z.record(
      z.string(),
      z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)])),
    ),
    z.array(z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
  ]),
);

export const JsonFilterSchema = z
  .object({
    equals: InputJsonValueSchema.optional(),
    path: z.string().array().optional(),
    string_contains: z.string().optional(),
    string_starts_with: z.string().optional(),
    string_ends_with: z.string().optional(),
    array_contains: InputJsonValueSchema.optional().nullable(),
    array_starts_with: InputJsonValueSchema.optional().nullable(),
    array_ends_with: InputJsonValueSchema.optional().nullable(),
    lt: InputJsonValueSchema.optional(),
    lte: InputJsonValueSchema.optional(),
    gt: InputJsonValueSchema.optional(),
    gte: InputJsonValueSchema.optional(),
    not: InputJsonValueSchema.optional(),
  })
  .strict();

export const NestedJsonFilterSchema = z
  .object({
    equals: InputJsonValueSchema.optional(),
    path: z.string().array().optional(),
    string_contains: z.string().optional(),
    string_starts_with: z.string().optional(),
    string_ends_with: z.string().optional(),
    array_contains: InputJsonValueSchema.optional().nullable(),
    array_starts_with: InputJsonValueSchema.optional().nullable(),
    array_ends_with: InputJsonValueSchema.optional().nullable(),
    lt: InputJsonValueSchema.optional(),
    lte: InputJsonValueSchema.optional(),
    gt: InputJsonValueSchema.optional(),
    gte: InputJsonValueSchema.optional(),
    not: InputJsonValueSchema.optional(),
  })
  .strict();

export const JsonNullableFilterSchema = z
  .object({
    equals: InputJsonValueSchema.optional(),
    path: z.string().array().optional(),
    string_contains: z.string().optional(),
    string_starts_with: z.string().optional(),
    string_ends_with: z.string().optional(),
    array_contains: InputJsonValueSchema.optional().nullable(),
    array_starts_with: InputJsonValueSchema.optional().nullable(),
    array_ends_with: InputJsonValueSchema.optional().nullable(),
    lt: InputJsonValueSchema.optional(),
    lte: InputJsonValueSchema.optional(),
    gt: InputJsonValueSchema.optional(),
    gte: InputJsonValueSchema.optional(),
    not: InputJsonValueSchema.optional(),
  })
  .strict();

export const BaseFilterSchema = z.object({
  take: z.coerce.number().optional(),
  skip: z.coerce.number().optional(),
});

export const BaseWhereSchema = (getSchema) =>
  z.object({
    AND: z.union([z.lazy(() => getSchema()), z.lazy(() => getSchema()).array()]).optional(),
    OR: z
      .lazy(() => getSchema())
      .array()
      .optional(),
    NOT: z.union([z.lazy(() => getSchema()), z.lazy(() => getSchema()).array()]).optional(),
  });

export const BaseRelationWhereSchema = <S extends z.Schema>(schema: S) =>
  z.object({
    every: schema.optional(),
    some: schema.optional(),
    none: schema.optional(),
  });

export const StringFilterFieldSchema = z.union([z.lazy(() => StringFilterSchema), z.string()]);
export const StringNullableFilterFieldSchema = z.union([
  z.lazy(() => StringNullableFilterSchema),
  z.string(),
]);
export const BoolFilterFieldSchema = z.union([z.lazy(() => BoolFilterSchema), BooleanSchema]);
export const NumberFilterFieldSchema = z.union([z.lazy(() => IntFilterSchema), z.coerce.number()]);
export const NumberNullableFilterFieldSchema = z.union([
  z.lazy(() => IntNullableFilterSchema),
  z.coerce.number(),
]);
export const DataTimeFilterFieldSchema = z.union([
  z.lazy(() => DateTimeFilterSchema),
  z.coerce.date(),
]);
export const JsonFilterFieldSchema = z.lazy(() => JsonFilterSchema);
export const JsonNullableFilterFieldSchema = z.lazy(() => JsonNullableFilterSchema);
